import React, { useEffect, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { TouchableOpacity } from 'react-native-gesture-handler';
import { useDispatch, useSelector } from 'react-redux';
import { openAlert } from '../../../store/actions/alert';
import { getExpenseAttendees } from '../../../store/actions/expense';
import { selectExpenseAttendees } from '../../../store/selectors/expense.selector';
import { Color, CommonProperties, CommonStyles } from '../../../styles';
import BaseText from '../../BaseText';
import BottomButton from '../../BottomButton';
import WrapBottomColor from '../../containers/WrapBottomColor';
import Icon from '../../Icon';
import InputForm from '../../InputForm';
import RowView from '../../RowView';
import { useTranslation } from 'react-i18next';
import { i18nNS, useComponentTKey } from '../../../i18n';

const AttendeesSelectorModal = ({ navigation, route }) => {
  const { t } = useTranslation(i18nNS.tripExpense);
  const { tKey } = useComponentTKey('ExpenseAddAttendess');
  const { returnScreen, attendees, userBelongToExpense } = route.params || {};
  const [userSelected, setUserSelected] = useState(userBelongToExpense);
  const [users, setUsers] = useState(attendees ? new Array(...attendees) : []);
  const [text, setText] = useState('');
  const dispatch = useDispatch();
  const oldAttendeesCreated = useSelector(selectExpenseAttendees);
  const [oldAttendees, setOldAttendees] = useState([]);

  useEffect(() => {
    dispatch(getExpenseAttendees());
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(attendees) && Array.isArray(oldAttendeesCreated)) {
      const selectedAttendees = attendees.map((x) => x.AttendeeId).filter((x) => x > 0);
      setOldAttendees(
        oldAttendeesCreated.filter((x) => selectedAttendees.indexOf(x.AttendeeId) < 0)
      );
    } else {
      setOldAttendees(oldAttendeesCreated);
    }
  }, [oldAttendeesCreated, attendees]);

  const onDelete = (attendee, index) => {
    const newUsers = [...users];
    newUsers.splice(index, 1);
    setUsers(newUsers);
    if (attendee.AttendeeId > 0) {
      setOldAttendees([...oldAttendees, attendee].sort((a, b) => a.Name - b.Name));
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const isValid = users.length > 0 || userSelected;
    if (isValid) {
      navigation.navigate(returnScreen, {
        selectedAttendees: users,
        userBelongToExpense: userSelected,
      });
    } else {
      dispatch(
        openAlert({
          title: 'Expense should have at least one attendee',
          confirmText: 'Continue',
          onConfirmPressed: () => {},
        })
      );
    }
  };

  const appendAttendee = (attendee) => {
    const index = oldAttendees.findIndex((x) => x.AttendeeId === attendee.AttendeeId);
    setUsers([...users, attendee]);
    const newList = [...oldAttendees];
    newList.splice(index, 1);
    setOldAttendees(newList);
  };

  const createNewAttendee = () => {
    if (text !== '') {
      setUsers([...users, { Name: text }]);
      setText('');
    }
  };

  return (
    <WrapBottomColor>
      <View style={styles.container}>
        <RowView style={CommonStyles.centerAlignment}>
          <InputForm
            styleContainer={styles.input}
            value={text}
            onChangeText={(text1) => setText(text1)}
          />
          <Icon name="search" size={22} color={Color.text.medium} />
        </RowView>
        {text != '' && text != null ? (
          <TouchableOpacity onPress={() => createNewAttendee()}>
            <RowView style={[CommonStyles.p3, CommonStyles.centerItems]}>
              <Icon name="plus" color={Color.primary} style={CommonStyles.mr3} />
              <BaseText style={styles.newAttendee}>Append "</BaseText>
              <BaseText style={styles.newAttendee}>{text}</BaseText>
              <BaseText style={styles.newAttendee}>" to Attendees</BaseText>
            </RowView>
          </TouchableOpacity>
        ) : null}
        <View style={CommonStyles.flex1}>
          <TouchableOpacity onPress={() => setUserSelected(!userSelected)}>
            <RowView style={styles.item}>
              <BaseText>{t(tKey('You'))}</BaseText>
              {userSelected ? <Icon name="check" color={Color.primary} /> : null}
            </RowView>
          </TouchableOpacity>
          {users
            ? users.map((user, index) => (
                <TouchableOpacity key={user.Name} onPress={() => onDelete(user, index)}>
                  <RowView style={styles.item}>
                    <BaseText>{user.Name}</BaseText>
                    <Icon name="check" color={Color.primary} />
                  </RowView>
                </TouchableOpacity>
              ))
            : null}
          {Array.isArray(oldAttendeesCreated) && oldAttendeesCreated.length > 0 ? (
            <View style={styles.title}>
              <BaseText style={styles.titleText}>{t(tKey('RecentlyCreated'))}</BaseText>
            </View>
          ) : null}
          {oldAttendees
            ? oldAttendees
                .filter((x) => x.Name.includes(text))
                .map((a) => (
                  <RowView key={a.AttendeeId} style={styles.item}>
                    <BaseText>{a.Name}</BaseText>
                    <TouchableOpacity onPress={() => appendAttendee(a)}>
                      <Icon name="plus" color={Color.primary} />
                    </TouchableOpacity>
                  </RowView>
                ))
            : null}
        </View>
      </View>
      <BottomButton full label={t(tKey('Save'))} onPress={onSubmit} />
    </WrapBottomColor>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'white',
    ...CommonProperties.px3,
    ...CommonProperties.py2,
  },
  input: {
    flex: 1,
    ...CommonProperties.px2,
  },
  item: {
    ...CommonProperties.p3,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  title: {
    backgroundColor: Color.grayText,
    ...CommonProperties.px3,
    ...CommonProperties.py2,
    ...CommonProperties.mt3,
    ...CommonProperties.borderRadius1,
  },
  titleText: {
    color: Color.accent,
  },
  newAttendee: {
    color: Color.primary,
  },
});

export default AttendeesSelectorModal;
